import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

// ==================== KPI Card Skeleton ====================
const useKPICardSkeletonStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  iconContainer: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  trend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
}));

export const KPICardSkeleton = () => {
  const classes = useKPICardSkeletonStyles();

  return (
    <Paper className={classes.paper} elevation={2}>
      <div className={classes.header}>
        <Box flex={1}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={40} />
        </Box>
        <Skeleton variant="rect" width={48} height={48} className={classes.iconContainer} />
      </div>
      <Box className={classes.trend}>
        <Skeleton variant="text" width={100} height={20} />
      </Box>
    </Paper>
  );
};

// ==================== KPICards Grid Skeleton ====================
const useKPICardsSkeletonStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginBottom: theme.spacing(3),
  },
}));

export const KPICardsSkeleton = () => {
  const classes = useKPICardsSkeletonStyles();

  return (
    <Grid container spacing={3} className={classes.root}>
      {/* 8 cards in total - 2 rows of 4 */}
      {[...Array(8)].map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <KPICardSkeleton />
        </Grid>
      ))}
    </Grid>
  );
};

// ==================== QuickStats Skeleton ====================
const useQuickStatsSkeletonStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
  },
  statIcon: {
    marginRight: theme.spacing(1.5),
  },
  statContent: {
    flex: 1,
  },
}));

export const QuickStatsSkeleton = () => {
  const classes = useQuickStatsSkeletonStyles();

  return (
    <Paper className={classes.paper}>
      <Box className={classes.title}>
        <Skeleton variant="circle" width={24} height={24} />
        <Skeleton variant="text" width={120} height={32} />
      </Box>

      <Box mb={2}>
        <Skeleton variant="text" width="80%" height={20} />
      </Box>

      <Grid container spacing={1}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} key={index}>
            <Box className={classes.statItem}>
              <Skeleton
                variant="circle"
                width={32}
                height={32}
                className={classes.statIcon}
              />
              <Box className={classes.statContent}>
                <Skeleton variant="text" width="30%" height={24} />
                <Skeleton variant="text" width="50%" height={16} />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

// ==================== Yearly Comparison Skeleton ====================
const useYearlyComparisonSkeletonStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  comparisonItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  progressContainer: {
    marginTop: theme.spacing(1),
  },
  values: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
  },
}));

export const YearlyComparisonSkeleton = () => {
  const classes = useYearlyComparisonSkeletonStyles();

  return (
    <Paper className={classes.paper}>
      <Skeleton variant="text" width={180} height={32} className={classes.title} />

      {[...Array(4)].map((_, index) => (
        <Box key={index} className={classes.comparisonItem}>
          <Box className={classes.itemHeader}>
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={60} height={20} />
          </Box>

          <Box className={classes.progressContainer}>
            <Skeleton variant="rect" width="100%" height={8} />
            <Box className={classes.values}>
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

// ==================== Status Distribution Chart Skeleton ====================
const useStatusDistributionSkeletonStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  chartContainer: {
    width: '100%',
    height: 200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
    width: '100%',
  },
}));

export const StatusDistributionSkeleton = () => {
  const classes = useStatusDistributionSkeletonStyles();

  return (
    <Paper className={classes.paper}>
      <Skeleton variant="text" width="60%" height={28} style={{ marginBottom: 16 }} />
      <Box className={classes.chartContainer}>
        <Skeleton variant="circle" width={150} height={150} />
      </Box>
      <Box className={classes.statsContainer}>
        <Skeleton variant="rect" width={120} height={32} />
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} variant="rect" width={100} height={24} />
        ))}
      </Box>
    </Paper>
  );
};

// ==================== Monthly Trends Chart Skeleton ====================
const useMonthlyTrendsSkeletonStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  chartContainer: {
    width: '100%',
    height: 300,
    marginBottom: theme.spacing(2),
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  summaryItem: {
    textAlign: 'center',
  },
}));

export const MonthlyTrendsSkeleton = () => {
  const classes = useMonthlyTrendsSkeletonStyles();

  return (
    <Paper className={classes.paper}>
      <Skeleton variant="text" width="40%" height={28} style={{ marginBottom: 16 }} />
      <Box className={classes.chartContainer}>
        <Skeleton variant="rect" width="100%" height={250} />
      </Box>
      <Box className={classes.summary}>
        {[...Array(4)].map((_, index) => (
          <Box key={index} className={classes.summaryItem}>
            <Skeleton variant="text" width={60} height={32} />
            <Skeleton variant="text" width={80} height={16} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ==================== Weekly Chart Skeleton (for old charts) ====================
const useWeeklyChartSkeletonStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
}));

export const WeeklyChartSkeleton = () => {
  const classes = useWeeklyChartSkeletonStyles();

  return (
    <Box className={classes.container}>
      <Skeleton variant="text" width="60%" height={28} className={classes.title} />
      <Skeleton variant="rect" width="100%" height={200} />
    </Box>
  );
};

// ==================== Full Dashboard Skeleton ====================
export const FullDashboardSkeleton = () => {
  return (
    <>
      <KPICardsSkeleton />
      <Grid container spacing={3}>
        <Grid item xs={12} lg={3} md={6}>
          <WeeklyChartSkeleton />
        </Grid>
        <Grid item xs={12} lg={3} md={6}>
          <QuickStatsSkeleton />
        </Grid>
        <Grid item xs={12} lg={3} md={6}>
          <StatusDistributionSkeleton />
        </Grid>
        <Grid item xs={12} lg={3} md={6}>
          <YearlyComparisonSkeleton />
        </Grid>
        <Grid item xs={12}>
          <MonthlyTrendsSkeleton />
        </Grid>
        <Grid item xs={12}>
          <Box p={2}>
            <Skeleton variant="text" width="40%" height={28} style={{ marginBottom: 16 }} />
            <Skeleton variant="rect" width="100%" height={300} />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default {
  KPICardSkeleton,
  KPICardsSkeleton,
  QuickStatsSkeleton,
  YearlyComparisonSkeleton,
  StatusDistributionSkeleton,
  MonthlyTrendsSkeleton,
  WeeklyChartSkeleton,
  FullDashboardSkeleton,
};
